import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Video, Users, Award, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import { trackCompletedRegistration } from '../lib/metaPixel';

// Cloudflare Turnstile Site Key
const TURNSTILE_SITE_KEY = '0x4AAAAAACMjo93RPVeeXOon';
const TURNSTILE_TIMEOUT_MS = 10000;

export default function Landing({ setUser, initialAuthMode = 'login', registrationAudience = 'general' }) {
  const location = useLocation();
  const requestedRole = new URLSearchParams(location.search).get('role')?.toLowerCase();
  const isEmployerRegistration = registrationAudience === 'employer' || ['company', 'employer'].includes(requestedRole);
  const startingAuthMode = isEmployerRegistration ? 'register' : initialAuthMode;
  const [showAuth, setShowAuth] = useState(startingAuthMode === 'register');
  const [registrationRole, setRegistrationRole] = useState(isEmployerRegistration ? 'company' : 'candidate');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const turnstileRef = useRef(null);
  const turnstileFallbackRef = useRef(null);
  const navigate = useNavigate();

  // Load Turnstile script
  useEffect(() => {
    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onerror = () => setTurnstileUnavailable(true);
      document.head.appendChild(script);
    } else if (window.turnstile) {
      setTurnstileReady(true);
    }
  }, []);

  // Render Turnstile widget when dialog opens on Sign Up tab
  useEffect(() => {
    let widgetId = null;
    
    const renderWidget = () => {
      if (showAuth && window.turnstile && turnstileRef.current) {
        setTurnstileReady(true);
        setTurnstileUnavailable(false);
        if (turnstileFallbackRef.current) {
          clearTimeout(turnstileFallbackRef.current);
        }
        // Clear any existing widget
        if (widgetId) {
          try {
            window.turnstile.remove(widgetId);
          } catch (e) {}
        }
        turnstileRef.current.innerHTML = '';
        setTurnstileToken(null);
        
        // Render new widget
        try {
          widgetId = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'light',
            callback: (token) => {
              if (turnstileFallbackRef.current) {
                clearTimeout(turnstileFallbackRef.current);
              }
              setTurnstileUnavailable(false);
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken(null);
            },
            'error-callback': () => {
              setTurnstileToken(null);
              setTurnstileUnavailable(true);
            }
          });
        } catch (e) {
          setTurnstileUnavailable(true);
        }
      }
    };
    
    if (showAuth) {
      setTurnstileReady(false);
      setTurnstileUnavailable(false);
      // Wait for turnstile to load
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          setTimeout(renderWidget, 100);
        }
      }, 100);

      turnstileFallbackRef.current = setTimeout(() => {
        const hasRenderedWidget = Boolean(turnstileRef.current?.querySelector('iframe'));
        if (!turnstileToken && !hasRenderedWidget) {
          setTurnstileUnavailable(true);
        }
      }, TURNSTILE_TIMEOUT_MS);
      
      // Cleanup
      return () => {
        clearInterval(checkTurnstile);
        if (turnstileFallbackRef.current) {
          clearTimeout(turnstileFallbackRef.current);
        }
        if (widgetId && window.turnstile) {
          try {
            window.turnstile.remove(widgetId);
          } catch (e) {}
        }
      };
    }
  }, [showAuth]);

  const completeAuthentication = (response, isRegistration = false) => {
    localStorage.setItem('token', response.token);
    localStorage.setItem('role', response.user.role);
    setUser(response.user);

    if (isRegistration) {
      trackCompletedRegistration(response.user);
    }

    toast.success(`Welcome ${response.user.full_name}!`);
    if (response.user.role === 'company') {
      navigate('/company/dashboard');
    } else if (response.user.role === 'admin') {
      navigate('/admin/dashboard');
    } else {
      navigate('/candidate/dashboard');
    }
  };

  const handleAuth = async (e, type) => {
    e.preventDefault();
    
    setLoading(true);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    // Add turnstile token for registration
    if (type === 'register' && turnstileToken) {
      data.turnstile_token = turnstileToken;
    }

    try {
      const endpoint = type === 'login' ? '/auth/login' : '/auth/register';
      const res = await api.post(endpoint, data);

      if (type === 'register' && res.data.verification_required) {
        setVerificationEmail(res.data.email);
        toast.success('Verification code sent. Check your email.');
        return;
      }

      completeAuthentication(res.data, type === 'register');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      const res = await api.post('/auth/verify-email', {
        email: verificationEmail,
        code: formData.get('code'),
      });
      completeAuthentication(res.data, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/resend-verification', { email: verificationEmail });
      toast.success(res.data.message || 'A new code has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
            <h1 className="text-2xl font-heading font-bold">The Interview</h1>
          </div>
          <Button data-testid="get-started-btn" onClick={() => setShowAuth(true)}>
            {isEmployerRegistration ? 'Create Employer Account' : 'Get Started'}
          </Button>
        </div>
      </header>

      <section className="hero-gradient text-white py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight leading-none">
              {isEmployerRegistration ? <><span>Meet Candidates.</span><br/><span>Start the Interview.</span></> : <><span>Skip the Application.</span><br/><span>Start the Interview.</span></>}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              {isEmployerRegistration
                ? 'Create roles, add interview availability, and meet candidates directly through live video interviews.'
                : 'Connect directly with hiring companies through video interviews. No more endless applications.'}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-gray-100 px-8 py-6 text-lg"
              data-testid="hero-cta-btn"
              onClick={() => setShowAuth(true)}
            >
              {isEmployerRegistration ? 'Start Hiring' : 'Get Hired Today'} <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">Trusted by forward-thinking companies</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold">How It Works</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-600 text-white rounded flex items-center justify-center mx-auto text-2xl font-bold">1</div>
              <h3 className="text-xl font-heading font-semibold">Create Profile</h3>
              <p className="text-muted-foreground">Companies create job listings with available interview slots</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-600 text-white rounded flex items-center justify-center mx-auto text-2xl font-bold">2</div>
              <h3 className="text-xl font-heading font-semibold">Book Slot</h3>
              <p className="text-muted-foreground">Candidates browse opportunities and book interview slots instantly</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-600 text-white rounded flex items-center justify-center mx-auto text-2xl font-bold">3</div>
              <h3 className="text-xl font-heading font-semibold">Interview</h3>
              <p className="text-muted-foreground">Join video interview with screen sharing for presentations</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="border p-8 space-y-4 card-hover bg-white">
              <Video className="w-12 h-12 text-blue-600" />
              <h3 className="text-2xl font-heading font-semibold">Video Interview Room</h3>
              <p className="text-muted-foreground">Professional video calls with screen sharing for presentations and resume reviews</p>
            </div>
            <div className="border p-8 space-y-4 card-hover bg-white">
              <Users className="w-12 h-12 text-blue-600" />
              <h3 className="text-2xl font-heading font-semibold">Community Hub</h3>
              <p className="text-muted-foreground">Connect with candidates and share interview tips in our supportive community</p>
            </div>
            <div className="border p-8 space-y-4 card-hover bg-white">
              <Award className="w-12 h-12 text-blue-600" />
              <h3 className="text-2xl font-heading font-semibold">Mentorship Program</h3>
              <p className="text-muted-foreground">Get matched with experienced mentors to improve your interview skills</p>
            </div>
            <div className="border p-8 space-y-4 card-hover bg-white">
              <Shield className="w-12 h-12 text-blue-600" />
              <h3 className="text-2xl font-heading font-semibold">Background Checks</h3>
              <p className="text-muted-foreground">Optional verified background checks to strengthen your profile</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">
              {verificationEmail ? 'Verify Your Email' : isEmployerRegistration ? 'Create Your Employer Account' : 'Get Started'}
            </DialogTitle>
            {!verificationEmail && isEmployerRegistration && (
              <p className="text-sm text-muted-foreground">Post opportunities, schedule interviews, and connect with candidates from one hiring workspace.</p>
            )}
          </DialogHeader>

          {verificationEmail && (
            <form onSubmit={handleEmailVerification} className="space-y-4" data-testid="email-verification-form">
              <p className="text-sm text-muted-foreground">
                Enter the six-digit code sent to <span className="font-medium text-foreground">{verificationEmail}</span>.
              </p>
              <div>
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  id="verification-code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  required
                  data-testid="verification-code-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="verify-email-btn">
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>
              <div className="flex justify-between gap-3">
                <Button type="button" variant="ghost" onClick={() => setVerificationEmail('')} disabled={loading}>Back</Button>
                <Button type="button" variant="outline" onClick={resendVerificationCode} disabled={loading}>Resend Code</Button>
              </div>
            </form>
          )}

          <Tabs defaultValue={startingAuthMode} className={verificationEmail ? 'hidden' : 'w-full'}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{isEmployerRegistration ? 'Employer Sign Up' : 'Sign Up'}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={(e) => handleAuth(e, 'login')} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" name="email" type="email" required data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input id="login-password" name="password" type="password" required data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Loading...' : 'Login'}
                </Button>
                <p className="text-center text-xs leading-5 text-muted-foreground" data-testid="login-consent-notice">
                  By signing in to The Interview, you agree to our <Link to="/terms" className="font-medium text-blue-700 underline">Terms of Service</Link> and acknowledge our <Link to="/privacy" className="font-medium text-blue-700 underline">Privacy Policy</Link>, including automated safety moderation.
                </p>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={(e) => handleAuth(e, 'register')} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Full Name</Label>
                  <Input id="register-name" name="full_name" required data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input id="register-email" name="email" type="email" autoComplete="email" required data-testid="register-email-input" />
                  <p className="mt-1 text-xs text-muted-foreground">Use an inbox you check. Temporary and test addresses are not accepted.</p>
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <Input id="register-password" name="password" type="password" autoComplete="new-password" minLength={10} maxLength={128} required data-testid="register-password-input" />
                  <p className="mt-1 text-xs text-muted-foreground">Use at least 10 characters.</p>
                </div>
                <div>
                  <Label htmlFor="register-role">I am a</Label>
                  <select 
                    id="register-role" 
                    name="role" 
                    value={registrationRole}
                    onChange={(event) => setRegistrationRole(event.target.value)}
                    required 
                    className="w-full border rounded px-3 py-2"
                    data-testid="register-role-select"
                  >
                    <option value="candidate">Job Seeker / Candidate</option>
                    <option value="company">Employer / Company</option>
                  </select>
                </div>
                {registrationRole === 'company' && (
                  <div id="company-name-field">
                    <Label htmlFor="register-company">Company Name</Label>
                    <Input id="register-company" name="company_name" required data-testid="register-company-input" />
                  </div>
                )}
                
                <div className="rounded border bg-gray-50 p-2">
                  <div ref={turnstileRef} className="flex justify-center min-h-10" data-testid="turnstile-widget"></div>
                  {!turnstileToken && !turnstileUnavailable && (
                    <p className="text-xs text-center text-muted-foreground">
                      {turnstileReady ? 'Complete the verification above to sign up' : 'Loading secure verification...'}
                    </p>
                  )}
                  {turnstileUnavailable && (
                    <p className="text-xs text-center text-amber-700">
                      Verification is unavailable right now. You can continue.
                    </p>
                  )}
                </div>
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  {loading ? 'Creating Account...' : registrationRole === 'company' ? 'Create Employer Account' : 'Create Candidate Account'}
                </Button>
                <p className="text-center text-xs leading-5 text-muted-foreground" data-testid="register-consent-notice">
                  By creating an account, you agree to our <Link to="/terms" className="font-medium text-blue-700 underline">Terms of Service</Link> and acknowledge our <Link to="/privacy" className="font-medium text-blue-700 underline">Privacy Policy</Link> and Community Safety rules.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
